import React from 'react';
import { Grid, Typography, Card, CardContent, Box, Skeleton } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import styled from '@emotion/styled';
import { useHasPermission } from '../utils/permissionUtils';
import StatCard from './StatCard';

const StyledGridItem = styled(Grid)(({ theme }) => ({
    width: '100%',
}));

const StyledCard = styled(Card)(({ theme }) => ({
    boxShadow: theme.shadows[3],
}));

const ProjectStatusContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    '& > *': {
        flex: '1 1 0',
        minWidth: 120,
    },
}));

const PercentageBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(1),
        width: "100%",
    },
}));

const PercentageBar = styled(Box)(({ theme }) => ({
    width: "100%",
    marginRight: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
        width: "100%",
        marginLeft: theme.spacing(1),
    },
}));

const PercentageText = styled(Box)(({ theme }) => ({
    minWidth: 35,
    [theme.breakpoints.down("sm")]: {
        minWidth: 35,
    },
}));

const ProgressRow = styled(Box)({
    width: '100%',
});

function LinearProgressWithLabel(props, value) {
    return (
        <PercentageBox>
            <PercentageBar>
                <LinearProgress variant="determinate" {...props} />
            </PercentageBar>
            <PercentageText>
                <Typography variant="body2" color="text.secondary">{`${Math.round(
                    props.value,
                )}%`}</Typography>
            </PercentageText>
        </PercentageBox>
    );
}

const AccountWideInfoBox = ({ accountData, accountProjects, loading, status }) => {
    const percentage = accountData.projects ? (100 - ((accountData.openProjects * 100) / accountData.projects)) : 0;
    const overdueProjects = status === 'open' ? accountProjects.filter(project => project.remainingDays === 'Overdue') : accountProjects.filter(project => project.actualDuration > project.expectedDuration);
    const dueTodayProjects = accountProjects.filter(project => project.remainingDays === '1 Day');
    const upcomingProjects = accountProjects.filter(project => project.remainingDays === '2 Days' || project.remainingDays === '3 Days');
    const onTimeProjects = accountProjects.filter(project => project.actualDuration === project.expectedDuration);
    const aheadOfTimeProjects = accountProjects.filter(project => project.actualDuration < project.expectedDuration);
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission('canSeeInternal');

    return (
        <StyledGridItem item>
            <StyledCard>
                <CardContent>
                    {canSeeInternal &&
                        <ProgressRow>
                            <Typography variant="caption">Progress: </Typography>
                            <LinearProgressWithLabel value={percentage} />
                        </ProgressRow>
                    }
                    <ProjectStatusContainer>
                        {canSeeInternal ? (
                            <StatCard size="compact" tone="error" label="Overdue" value={loading ? <Skeleton variant="text" width={40} /> : overdueProjects.length} />
                        ) : status === 'open' ? (
                            <StatCard size="compact" tone="neutral" label="Open Projects" value={loading ? <Skeleton variant="text" width={40} /> : accountData.openProjects} />
                        ) : (
                            <StatCard
                                size="compact"
                                tone="neutral"
                                label="Completed Projects"
                                value={loading ? <Skeleton variant="text" width={40} /> : accountData.projects - accountData.openProjects}
                            />
                        )}
                        {status === 'open' && (
                            <>
                                <StatCard size="compact" tone="neutral" label="Due Today" value={loading ? <Skeleton variant="text" width={40} /> : dueTodayProjects.length} />
                                {canSeeInternal && (
                                    <StatCard size="compact" tone="neutral" label="Upcoming" value={loading ? <Skeleton variant="text" width={40} /> : upcomingProjects.length} />
                                )}
                            </>
                        )}
                        {status === 'completed' && (
                            <>
                                <StatCard size="compact" tone="neutral" label="On Time" value={loading ? <Skeleton variant="text" width={40} /> : onTimeProjects.length} />
                                <StatCard size="compact" tone="success" label="Early" value={loading ? <Skeleton variant="text" width={40} /> : aheadOfTimeProjects.length} />
                            </>
                        )}
                    </ProjectStatusContainer>
                </CardContent>
            </StyledCard >
        </StyledGridItem >
    );
};

export default AccountWideInfoBox;
